import { useEffect, useRef, useState } from "react";

import { ErrorText } from "~/ui/common/ErrorText";
import { tw } from "~/utils";

export interface MultiSelectOption {
  id: string;
  label: string;
  description?: string;
  forcesSingleSelection?: boolean;
}

interface MultiSelectCardGroupPros {
  options: MultiSelectOption[];
  onChange: (selectedOptions: string[]) => void;
  label: string;
  errorId: string;
  defaultSelection?: string[];
  error?: string;
  submitAttempts?: number;
}

export const MultiSelectCardGroup = ({
  options,
  onChange,
  label,
  errorId,
  defaultSelection = [],
  error,
  submitAttempts,
}: MultiSelectCardGroupPros) => {
  const [selectedOptions, setSelectedOptions] =
    useState<string[]>(defaultSelection);
  const firstOptionRef = useRef<HTMLInputElement>(null);

  const toggleOption = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = e.target.value;

    setSelectedOptions((prev) => {
      const option = options.find(
        (option) => option.id === id,
      ) as MultiSelectOption;

      if (prev.includes(id)) {
        return prev.filter((planId) => planId !== id);
      }

      if (option.forcesSingleSelection) {
        return [id];
      }

      return [...prev, id].filter((optionId) => {
        const option = options.find(
          (option) => option.id === optionId,
        ) as MultiSelectOption;

        return !option.forcesSingleSelection;
      });
    });
  };

  useEffect(() => {
    onChange(selectedOptions);
  }, [selectedOptions]);

  useEffect(() => {
    if (error) firstOptionRef.current?.focus();
  }, [error, submitAttempts]);

  return (
    <fieldset aria-label={label}>
      <div className="space-y-4">
        {options.map((option, index) => {
          const isChecked = selectedOptions.includes(option.id);
          return (
            <label
              key={option.id}
              aria-label={option.label}
              aria-description={option.description ?? option.label}
              className={tw(
                "group relative block w-full cursor-pointer rounded-lg border border-gray-200 bg-white p-3 text-left text-gray-700 transition-all duration-200 hover:border-violet-300 hover:bg-neutral-100/50",
                "has-[:focus]:ring-2 has-[:focus]:ring-gray-900 has-[:focus]:ring-offset-2",
                isChecked && "border-violet bg-violet/10",
              )}
            >
              <input
                ref={index === 0 ? firstOptionRef : undefined}
                type="checkbox"
                value={option.id}
                checked={isChecked}
                onChange={toggleOption}
                aria-invalid={!!error}
                aria-describedby={error ? errorId : undefined}
                className="absolute inset-0 cursor-pointer appearance-none focus:outline-none"
              />
              <span className="text-indigo block text-base font-medium">
                {option.label}
              </span>
              {option.description && (
                <span className="text-indigo-charcoal mt-1 block text-sm">
                  {option.description}
                </span>
              )}
            </label>
          );
        })}
      </div>
      {error && (
        <ErrorText id={errorId} className="mt-3">
          {error}
        </ErrorText>
      )}
    </fieldset>
  );
};
